import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useFetcher } from '@remix-run/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useStep } from 'usehooks-ts';
import { z } from 'zod';

import { Button } from '~/components/ui/button';
import { blurIn } from '~/utils';

import { EmailForm } from './EmailForm';
import { PasswordForm } from './PasswordForm';

const schema = z.object({
  email: z.string().email(),
  password: z.string(),
});

type FormValues = z.infer<typeof schema>;

const LoginView = () => {
  const fetcher = useFetcher();

  const [step, { goToNextStep, goToPrevStep }] = useStep(3);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleGoogleLogin = () => {
    fetcher.submit(
      {},
      {
        method: 'post',
        action: '/auth/google',
      },
    );
  };

  return (
    <AnimatePresence mode="popLayout">
      <div className="flex w-full flex-col gap-6">
        {step === 1 && (
          <motion.div
            key="intro"
            {...blurIn}
            className="flex w-full flex-col items-center gap-6"
          >
            <p className="text-neutral-350 text-xl font-medium tracking-tight">
              Log in to Raffle
            </p>
            <div className="flex w-full flex-col gap-4">
              <Button
                variant="secondary"
                type="button"
                onClick={handleGoogleLogin}
                className="bg-accent-blue-700 hover:bg-accent-blue-800 active:bg-accent-blue-900 h-14 w-full border-none text-neutral-300"
              >
                Continue with Google
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={goToNextStep}
                className="bg-primary-500 border-primary-450 hover:bg-primary-550 h-14 text-neutral-300"
              >
                Continue with work email
              </Button>
            </div>
            <p className="text-neutral-450 text-xs">
              New to Raffle?{' '}
              <Link
                to="/signup?step=form"
                className="text-neutral-350 font-medium decoration-dotted hover:underline"
              >
                Sign up
              </Link>
            </p>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="email" {...blurIn} className="w-full">
            <EmailForm form={form} onNext={goToNextStep} />
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="password" {...blurIn} className="w-full">
            <PasswordForm form={form} />
          </motion.div>
        )}
        {step !== 1 && (
          <motion.button
            {...blurIn}
            key="back"
            type="button"
            onClick={goToPrevStep}
            className="text-xs font-medium text-neutral-300 decoration-dotted hover:underline"
          >
            Go back
          </motion.button>
        )}
      </div>
    </AnimatePresence>
  );
};

export default LoginView;
